import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Header.css';
import ContactCard from './ContactCard'; // Add this import

const Header = () => {
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showContactCard, setShowContactCard] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991.98);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  
  const locations = [
    'Goa',
    'Kerala',
    'Rajasthan',
    'Himachal Pradesh',
    'Kashmir',
    'Uttarakhand',
    'Tamil Nadu',
    'Karnataka'
  ];

  // Typing animation effect
  useEffect(() => {
    const typeSpeed = 100;
    const deleteSpeed = 50;
    const pauseTime = 2000;
    
    const timer = setTimeout(() => {
      const currentLocation = locations[currentIndex];
      
      if (!isDeleting) {
        // Typing
        if (currentText.length < currentLocation.length) {
          setCurrentText(currentLocation.slice(0, currentText.length + 1));
        } else {
          // Pause before deleting
          setTimeout(() => setIsDeleting(true), pauseTime);
        }
      } else {
        // Deleting
        if (currentText.length > 0) {
          setCurrentText(currentText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % locations.length);
        }
      }
    }, isDeleting ? deleteSpeed : typeSpeed);
    
    return () => clearTimeout(timer);
  }, [currentText, isDeleting, currentIndex, locations]);

  // Initialize Bootstrap components
  useEffect(() => {
    if (window.bootstrap) {
      // Initialize dropdowns
      const dropdownElementList = document.querySelectorAll('.dropdown-toggle');
      dropdownElementList.forEach(function (dropdownToggleEl) {
        new window.bootstrap.Dropdown(dropdownToggleEl);
      });
      
      // Initialize mobile menu close button
      const closeButton = document.querySelector('.btn-close');
      if (closeButton) {
        closeButton.addEventListener('click', () => {
          const navbarCollapse = document.getElementById('navbarNav');
          const bsCollapse = window.bootstrap.Collapse.getInstance(navbarCollapse);
          if (bsCollapse) {
            bsCollapse.hide();
          }
        });
      }
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const searchValue = searchInputRef.current.value.trim();
    if (searchValue) {
      // Navigate to trips page with search query parameter using React Router
      navigate(`/trips?q=${encodeURIComponent(searchValue)}`);
    }
  };

  // Contact Card Overlay logic
  useEffect(() => {
    window.showContactCardOverlay = () => setShowContactCard(true);
    return () => {
      delete window.showContactCardOverlay;
    };
  }, []);

  const handleCloseContactCard = () => setShowContactCard(false);

  // Update mobile view state on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991.98);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Contact Card Overlay */}
      {showContactCard && (
        <div
          className="contact-card-overlay"
          onClick={handleCloseContactCard}
        >
          <div
            className="contact-card-container"
            onClick={e => e.stopPropagation()}
          >
            <ContactCard
              isOverlay={true}
              onClose={handleCloseContactCard}
            />
          </div>
        </div>
      )}

      <nav className="navbar navbar-expand-lg travelscape-navbar">
        <div className="container">
          <a className="navbar-brand" href="/">
            <div className="logo-container">
              <img
                src="/assets/logo1.png"
                alt="Travelscape Logo"
                className="main-logo"
                style={{ height: '44px', maxHeight: '44px' }} // reduced from 60px
              />
              {/* Add logo2 to the right of logo1 */}
              <img
                src="/assets/logo2.png"
                alt="Travelscape Logo 2"
                className="main-logo logo2"
                style={{ marginLeft: '4px', height: '20px' }} // reduced from 38px
              />
            </div>
          </a>

          {/* Mobile: show search icon left of menu icon, expand on tap */}
          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
              {!mobileSearchOpen ? (
                <>
                  <button
                    type="button"
                    className="btn p-0"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      fontSize: '22px',
                      marginRight: '18px' // increased gap between search and menu icon
                    }}
                    aria-label="Open search"
                    onClick={() => setMobileSearchOpen(true)}
                  >
                    <i className="fa-solid fa-magnifying-glass"></i>
                  </button>
                  <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
                    <span className="navbar-toggler-icon"></span>
                  </button>
                </>
              ) : (
                <form
                  className="d-flex search-form-center mobile-search-bar"
                  onSubmit={e => {
                    handleSearch(e);
                    setMobileSearchOpen(false);
                  }}
                  style={{
                    marginLeft: '0',
                    marginRight: '10px',
                    width: '80%', // changed from 60% to 80%
                    transition: 'width 0.3s',
                  }}
                >
                  <div className="search-input-container" style={{ width: '100%' }}>
                    <input
                      className="form-control search-input"
                      type="search"
                      placeholder={`Search for ${currentText}`}
                      aria-label="Search"
                      ref={searchInputRef}
                      autoFocus
                      style={{ width: '100%' }}
                      onBlur={() => setMobileSearchOpen(false)}
                    />
                    <i
                      className="search-icon fa-solid fa-magnifying-glass"
                      style={{ opacity: 0.8, cursor: 'pointer' }}
                      onClick={() => {
                        const searchValue = searchInputRef.current.value.trim();
                        if (searchValue) {
                          navigate(`/trips?q=${encodeURIComponent(searchValue)}`);
                          setMobileSearchOpen(false);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label="Search"
                    ></i>
                    {/* Add search button for mobile */}
                    <button
                      type="submit"
                      className="btn btn-primary mobile-search-btn"
                      style={{
                        marginLeft: '8px',
                        borderRadius: '20px',
                        padding: '6px 18px',
                        fontSize: '15px',
                        background: 'var(--primary-color, #007bff)',
                        border: 'none',
                        color: 'white',
                        fontWeight: 500,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      }}
                    >
                      Search
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Desktop: normal toggler and search bar */}
          {!isMobile && (
            <>
              <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
                <span className="navbar-toggler-icon"></span>
              </button>
            </>
          )}

          <div className="collapse navbar-collapse" id="navbarNav">
            <div className="d-flex justify-content-between align-items-center d-lg-none mobile-nav-header">
              <a className="navbar-brand" href="/">
                <div className="logo-container">
                  <img
                    src="/assets/logo1.png"
                    alt="Travelscape Logo"
                    className="mobile-logo"
                    style={{ height: '28px' }} // reduced from 38px
                  />
                  {/* Add logo2 to the right of logo1 in mobile */}
                  <img
                    src="/assets/logo2.png"
                    alt="Travelscape Logo 2"
                    className="mobile-logo logo2"
                    style={{ marginLeft: '6px', height: '20px' }} // reduced from 28px
                  />
                </div>
              </a>
              <button type="button" className="btn-close text-reset" data-bs-dismiss="collapse" data-bs-target="#navbarNav" aria-label="Close"></button>
            </div>
            
            {/* Flexible space - reduced */}
            <div className="navbar-space"></div>
            
            {/* Desktop search bar only */}
            {!isMobile && (
              <form className="d-flex search-form-center" onSubmit={handleSearch}>
                <div className="search-input-container">
                  <i
                    className="search-icon fa-solid fa-magnifying-glass"
                    style={{ opacity: 0.8, cursor: 'pointer' }}
                    onClick={() => {
                      const searchValue = searchInputRef.current.value.trim();
                      if (searchValue) {
                        navigate(`/trips?q=${encodeURIComponent(searchValue)}`);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label="Search"
                  ></i>
                  <input 
                    className="form-control search-input" 
                    type="search" 
                    placeholder={`Search for ${currentText}`}
                    aria-label="Search"
                    ref={searchInputRef}
                  />
                </div>
              </form>
            )}
            
            {/* Right side navigation links */}
            <ul className="navbar-nav travelscape-navbar-nav ms-auto">
              <li className="nav-item">
                <a className="nav-link" href="/">Home</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="/trips?q=group">Group Trips</a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowContactCard(true);
                  }}
                >
                  Customized Trips
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;